import { EntityType } from "~/common/enums";
import { messageBoardHelper } from "../MessageBoard/MessageBoardHelper";
import { DetailsEntity } from "./types";

export const defaultFocusedElement = "name";

export type ScrollToMessageOptions = {
    scrollToForcedMessage: {
        forcedMessageId: number;
        isAttachment: boolean;
    };
};

type QueryParams = {
    /**
     * Notification center EventId. Used to track selected row on notification center page.
     */
    EventId?: number;
    /**
     * If true, the page will scroll to the last message.
     */
    messageBoard?: boolean;
};

const supportedQueryParams: (keyof QueryParams)[] = ["EventId", "messageBoard"];

export class DetailsId {
    id: number;
    entity: DetailsEntity;
    focusedElement: string;
    isTask: boolean;
    isProject: boolean;
    isForm: boolean;
    scrollToMessageOptions: ScrollToMessageOptions = null;
    queryParams: QueryParams = null;

    constructor(id: number, entity: DetailsEntity) {
        this.id = id;
        this.entity = entity;
        this.isTask = this.entity === EntityType.Task;
        this.isProject = this.entity === EntityType.Project;
        this.isForm = this.entity === EntityType.Form;
        this.focusedElement = defaultFocusedElement;
    }

    equals(another: DetailsId) {
        if (!another) {
            return false;
        }

        return this.id === another.id && this.entity === another.entity;
    }

    withScrollToMessage(messageId: number) {
        if (messageId) {
            const options = messageBoardHelper.getScrollToForcedOptions(messageId, false);
            this.withScrollToMessageOptions(options);
        }

        return this;
    }

    withScrollToMessageOptions(options: ScrollToMessageOptions) {
        this.scrollToMessageOptions = options;
        return this;
    }

    withFocusedElement(focusElement: string) {
        if (focusElement) {
            this.focusedElement = focusElement;
        }
        return this;
    }

    getScrollToMessageId() {
        if (this.scrollToMessageOptions) {
            return this.scrollToMessageOptions.scrollToForcedMessage.forcedMessageId;
        }

        return null;
    }

    isScrollToAttachment() {
        if (this.scrollToMessageOptions) {
            return this.scrollToMessageOptions.scrollToForcedMessage.isAttachment;
        }

        return false;
    }

    getScrollToMessageInfo() {
        if (this.scrollToMessageOptions) {
            return this.scrollToMessageOptions.scrollToForcedMessage;
        }

        return null;
    }

    static forTask(id: number) {
        return new DetailsId(id, EntityType.Task);
    }

    static forProject(id: number) {
        return new DetailsId(id, EntityType.Project);
    }

    static forUser(id: number) {
        return new DetailsId(id, EntityType.User);
    }

    static forPortfolio(id: number) {
        return new DetailsId(id, EntityType.Portfolio);
    }

    static forForm(id: number) {
        return new DetailsId(id, EntityType.Form);
    }

    static get supportedQueryParams() {
        return supportedQueryParams;
    }
}

// @ts-ignore
registerNS("ep.details");
// @ts-ignore
ep.details.DetailsId = DetailsId;
